import React, { type FC, type RefObject } from "react"
import clsx from "classnames"
import type { StripeCardElementOptions, StripeIbanElementOptions } from "@stripe/stripe-js"

interface TextFieldProps {
  autoComplete?: boolean | string
  classes?: string
  disabled?: boolean
  error?: boolean
  fullWidth?: boolean
  helperText?: string
  id: string
  inputComponent?: React.ElementType
  inputProps?: { options?: StripeCardElementOptions | StripeIbanElementOptions }
  inputRef?: RefObject<HTMLInputElement>
  label?: string
  name: string
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  pattern?: string
  placeholder?: string
  type?: string
  value: string
}

const TextField: FC<TextFieldProps> = ({
  autoComplete = true,
  classes = "",
  disabled = false,
  error = false,
  fullWidth,
  helperText = "",
  id,
  inputComponent = "input",
  inputProps = {},
  inputRef = null,
  label,
  name,
  onBlur,
  // Passing a value to a field without an onChange handler causes a warning
  // that you are creating a read-only field. Therefore, we'll ensure that
  // onChange has a default value
  onChange = () => {},
  placeholder,
  pattern,
  type = "text",
  value,
}) => {
  let autoCompleteText: string | boolean = autoComplete
  if (autoCompleteText === true) {
    autoCompleteText = "on"
  } else if (autoCompleteText === false) {
    autoCompleteText = "off"
  }
  const InputComponent = inputComponent
  return (
    <div
      className={clsx(
        "relative m-0 inline-flex min-w-0 flex-col border-0 p-0 align-top text-gray-500 transition-colors focus-within:text-blue-600",
        { "text-red-700": error },
        { "w-full": fullWidth },
      )}
      data-testid="outer-container"
    >
      {label && (
        <label
          className={clsx("font-noto mb-2 p-0 text-left text-xs leading-none font-semibold", {
            "text-red-700": error,
            "text-gray-400": disabled && !error,
          })}
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <div
        className={clsx(
          "font-noto relative box-border inline-flex items-center overflow-hidden rounded-sm border border-solid bg-white font-normal focus-within:border-blue-600",
          classes,
          { "border-gray-400": !error },
          { "border-red-700": error },
        )}
        data-testid="input-container"
      >
        <InputComponent
          autoComplete={autoCompleteText}
          className={clsx(
            "font-inherit tracking-inherit relative m-0 box-content block h-4 w-full min-w-0 appearance-none items-center border-0 bg-white bg-none px-3 py-3.5 text-base text-current max-sm:!text-base",
            { "cursor-text text-gray-800": !disabled },
            { "cursor-default text-gray-400": disabled },
          )}
          disabled={disabled}
          id={id}
          {...inputProps}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          pattern={pattern}
          placeholder={placeholder}
          ref={inputRef}
          type={type}
          value={value}
        />
      </div>
      {helperText && (
        <div
          className={clsx("font-noto mt-1 text-xs font-normal", {
            "text-red-700": error,
          })}
        >
          {helperText}
        </div>
      )}
    </div>
  )
}

export default TextField
